import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils, comnEnvs } from "@/comn/utils";
import { Page, Group, Layout, Button, Grid } from "@/comn/components";
import { useForm, useFetch, useStore, useToast, useModal, useResource, useGrid, usePage, useAuth } from "@/comn/hooks";
import { ptlUtils } from "@/tz/go/egaz/osci/ptl/comn";
import {
    BASE,
    URLS,
    APIS,
    SF_OGA_USR_SRCH,
    SG_OGA_USR_LIST,
    SG_OGA_USR_EXCEL_LIST,
} from "./services/OsciUsrRoleService";
import { Link } from "react-router-dom";
import dayjs from "dayjs";

/**
 * Internal role List
 * !==  내부 사용자 역할 목록 ==!
 *
 */
export const OSCI0104025Q = (props: any) => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0003", "auth_osci0002"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const [load, setLoad] = useState(false);
    const auth = useAuth();

    // auth_osci0003(OSC 관리자) 권한 보유 여부 !== 미보유 시 조회 조건 orgCd 를 본인 조직으로 고정 ==!
    const hasOscAdminAuth = ptlUtils.hasAuth("auth_osci0003", auth);

    useResource({
        defaultSchema: [{ area: "roleCd" }, { area: "orgCd" }, { area: "deptCd" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        ogaUsrSrch: useForm({
            defaultSchema: SF_OGA_USR_SRCH,
            defaultValues: {
                // 최초 요청부터 orgCd 가 실려야 useFetch 자동조회와의 경합을 피할 수 있음
                ...(!hasOscAdminAuth ? { orgCd: auth.get("orgCd") } : {}),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        gridOgaUsrList: useGrid({
            defaultSchema: SG_OGA_USR_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        gridOgaUsrExcelList: useGrid({
            defaultSchema: SG_OGA_USR_EXCEL_LIST,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        getOgaUsrList: useFetch({
            api: (page = grid.gridOgaUsrList.page, size = grid.gridOgaUsrList.size) =>
                APIS.getOgaUsrList(form.ogaUsrSrch.getValues(), page, size),
            enabled: form.ogaUsrSrch.isSubmitted,
            key: [grid.gridOgaUsrList.page, grid.gridOgaUsrList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    ...pgeStore,
                    form: form.ogaUsrSrch.getValues(),
                    page: grid.gridOgaUsrList.page,
                    size: grid.gridOgaUsrList.size,
                });
            },
            onError: () => modal.openModal({ content: "msg.osc.00043" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),

        // Get a excel list of Customs Officer. !== 세관직원 엑셀 목록을 조회한다. ==!
        getOgaUsrExcelList: useFetch({
            api: (data: any) => (load ? APIS.getOgaUsrExcelList(data) : null),
            enabled: comnUtils.isEmpty(form.ogaUsrSrch.errors),
            onSuccess: () => {
                if (load) {
                    grid.gridOgaUsrExcelList.exportExcel({
                        excelName: `OGA User Role_${t("T_USR_INFO")}_${dayjs().format("DD/MM/YYYY HHmm")}`,
                        sheetName: "Sheet1",
                    });
                }
                setLoad(false);
            },
            onError: () => {},
            showToast: true,
        }),
    };
    const render = {
        ogaUsrList: {
            cell: {
                usrNm: (props: any) => {
                    const { value, rowValues } = props;
                    return <Link to={`${URLS.osci0104026s}/${rowValues.usrIdntfId}`}>{value}</Link>;
                },
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        onSrchClick: form.ogaUsrSrch.handleSubmit(() => fetch.getOgaUsrList.fetch()),
        onClear: () => {
            form.ogaUsrSrch.reset();

            if (!hasOscAdminAuth) {
                form.ogaUsrSrch.setValue("orgCd", auth.get("orgCd"));
                form.ogaUsrSrch.setSchema("orgCd", { edit: false });
            }
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        handler.onSrchClick(pgeStore?.page);
    }, []);

    useEffect(() => {
        pgeStore?.form && form.ogaUsrSrch.setValues(pgeStore.form);

        if (!hasOscAdminAuth) {
            form.ogaUsrSrch.setValue("orgCd", auth.get("orgCd"));
            form.ogaUsrSrch.setSchema("orgCd", { edit: false });
        }

        pgeStore?.form && handler.onSrchClick();
    }, []);

    useEffect(() => {
        const store = getStore(pgeUid);
        if (!comnUtils.isEmpty(store) || load) {
            fetch.getOgaUsrExcelList.fetch(store.form);
        }
    }, [load]);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.ogaUsrSrch.schema.orgCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.ogaUsrSrch.schema.usrIdntfNo} />
                            <Group.Control {...form.ogaUsrSrch.schema.usrNm} />
                        </Group.Row>
                    </Group.Section>
                    <Layout>
                        <Layout.Left>
                            <Button role={"reset"} onClick={handler.onClear} />
                        </Layout.Left>
                        <Layout.Right>
                            <Button role={"search"} onClick={handler.onSrchClick} />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    {
                        <Layout direction="row">
                            <Layout.Left>
                                <Button
                                    role="excelDown"
                                    onClick={() => {
                                        setLoad(true);
                                    }}
                                ></Button>
                            </Layout.Left>
                        </Layout>
                    }
                    {/* <Group.Title title={"T_CSTM_OFCR_LIST"} /> */}
                    <Grid
                        {...grid.gridOgaUsrList.grid}
                        data={fetch.getOgaUsrList.data?.ogaUsrList}
                        render={render.ogaUsrList}
                    />
                </Group.Body>
            </Group>
            <Group hidden>
                <Grid {...grid.gridOgaUsrExcelList.grid} data={fetch.getOgaUsrExcelList.data?.ogaUsrExcelList} />
            </Group>
        </Page>
    );
};
